import React, { useEffect, useRef, useCallback, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Box, IconButton, Tooltip, Divider, CircularProgress, Popover } from '@mui/material'
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Link as LinkIcon,
  Image as ImageIcon,
  EmojiEmotions,
  AutoFixHigh,
} from '@mui/icons-material'
import { api } from '../../services/Main/Api'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'

const MenuBar = ({ editor, onImageClick, onEmojiClick, onImproveClick, improving, showAIImprove = true }) => {
  if (!editor) return null

  const btnSx = (isActive) => ({
    p: 0.5,
    borderRadius: 0.75,
    color: isActive ? '#0073ea' : '#94A3B8',
    bgcolor: isActive ? '#EFF6FF' : 'transparent',
    '&:hover': { bgcolor: '#F1F5F9', color: '#475569' },
  })

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
      return
    }
    const url = window.prompt('URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  return (
    <Box display="flex" alignItems="center" gap={0.25} px={1} py={0.5} sx={{ borderBottom: '1px solid #E2E8F0' }}>
      <Tooltip title="Negrito (Ctrl+B)" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} sx={btnSx(editor.isActive('bold'))}><FormatBold sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Itálico (Ctrl+I)" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} sx={btnSx(editor.isActive('italic'))}><FormatItalic sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Tachado" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleStrike().run()} sx={btnSx(editor.isActive('strike'))}><FormatStrikethrough sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Código" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleCode().run()} sx={btnSx(editor.isActive('code'))}><Code sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <Tooltip title="Lista" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} sx={btnSx(editor.isActive('bulletList'))}><FormatListBulleted sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Lista numerada" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} sx={btnSx(editor.isActive('orderedList'))}><FormatListNumbered sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Citação" arrow><IconButton size="small" onClick={() => editor.chain().focus().toggleBlockquote().run()} sx={btnSx(editor.isActive('blockquote'))}><FormatQuote sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Link" arrow><IconButton size="small" onClick={handleLink} sx={btnSx(editor.isActive('link'))}><LinkIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Inserir imagem" arrow><IconButton size="small" onClick={onImageClick} sx={btnSx(false)}><ImageIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Tooltip title="Emoji" arrow><IconButton size="small" onClick={onEmojiClick} sx={btnSx(false)}><EmojiEmotions sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      {showAIImprove && <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />}
      {showAIImprove && <Tooltip title="Melhorar texto com IA" arrow>
        <IconButton size="small" onClick={onImproveClick} disabled={improving} sx={{ ...btnSx(false), color: improving ? '#7C3AED' : '#94A3B8', '&:hover': { bgcolor: '#F5F3FF', color: '#7C3AED' } }}>
          {improving ? <CircularProgress size={16} sx={{ color: '#7C3AED' }} /> : <AutoFixHigh sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>}
    </Box>
  )
}

const MarkdownEditor = React.forwardRef(({ value, onChange, placeholder = 'Digite sua resposta...', minHeight = 80, disabled = false, ticketId, showAIImprove = true }, editorRef) => {
  const imageInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [improving, setImproving] = useState(false)
  const [emojiAnchor, setEmojiAnchor] = useState(null)

  // Track inline image attachment IDs for later linking to comment
  const inlineAttachmentIds = React.useRef([])

  const getAndClearInlineAttachmentIds = useCallback(() => {
    const ids = [...inlineAttachmentIds.current]
    inlineAttachmentIds.current = []
    return ids
  }, [])

  // Expose method to parent via ref or callback
  React.useImperativeHandle(editorRef, () => ({
    getInlineAttachmentIds: getAndClearInlineAttachmentIds,
  }))

  const uploadImage = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) return null
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (ticketId) {
        formData.append('ticket', ticketId)
      }
      const res = await api.post(`tickets/${ticketId}/attachments/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // Track attachment ID for later linking to comment
      if (res.data.id) {
        inlineAttachmentIds.current.push(res.data.id)
      }
      return res.data.file
    } catch (err) {
      console.error('Upload failed:', err)
      return null
    } finally {
      setUploading(false)
    }
  }, [ticketId])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          style: 'max-width: 100%; border-radius: 6px; margin: 8px 0;',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const text = htmlToMarkdown(html)
      onChange?.(text)
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
          if (imageFiles.length > 0) {
            event.preventDefault()
            imageFiles.forEach(async (file) => {
              const url = await uploadImage(file)
              if (url) {
                view.dispatch(view.state.tr.replaceSelectionWith(
                  view.state.schema.nodes.image.create({ src: url })
                ))
              }
            })
            return true
          }
        }
        return false
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              event.preventDefault()
              const file = item.getAsFile()
              if (file) {
                uploadImage(file).then(url => {
                  if (url) {
                    view.dispatch(view.state.tr.replaceSelectionWith(
                      view.state.schema.nodes.image.create({ src: url })
                    ))
                  }
                })
              }
              return true
            }
          }
        }
        return false
      },
    },
  })

  const handleEmojiSelect = (emoji) => {
    if (editor) {
      editor.chain().focus().insertContent(emoji.native).run()
    }
    setEmojiAnchor(null)
  }

  const handleImproveText = async () => {
    if (!editor) return
    const currentText = htmlToMarkdown(editor.getHTML())
    if (!currentText.trim()) {
      alert('Digite algum texto antes de melhorar com IA.')
      return
    }

    setImproving(true)
    try {
      const res = await api.post('tickets/ai/improve-text/', { text: currentText })
      const improved = res.data?.improved_text
      if (improved) {
        editor.commands.setContent(markdownToHtml(improved))
        onChange?.(improved)
      } else if (res.data?.error) {
        alert(`Erro: ${res.data.error}`)
      }
    } catch (err) {
      console.error('AI improve failed:', err)
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Erro ao melhorar texto. Tente novamente.'
      alert(msg)
    } finally {
      setImproving(false)
    }
  }

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || [])
    // Upload all images first, then insert all at once
    const urls = []
    for (const file of files) {
      const url = await uploadImage(file)
      if (url) urls.push(url)
    }
    // Insert all images in a single transaction
    if (urls.length > 0 && editor) {
      editor.chain().focus().command(({ tr, dispatch }) => {
        const { schema } = editor.state
        if (dispatch) {
          for (const url of urls) {
            const imgNode = schema.nodes.image.create({ src: url })
            const paragraph = schema.nodes.paragraph.create()
            const pos = tr.doc.content.size - 1 // before last closing tag
            tr.insert(pos, imgNode)
            tr.insert(pos + 1, paragraph)
          }
        }
        return true
      }).run()
    }
    if (imageInputRef.current) imageInputRef.current.value = ''
  }

  // Sync external value changes (e.g., clearing after send)
  useEffect(() => {
    if (editor && !value && editor.getHTML() !== '<p></p>') {
      editor.commands.clearContent()
    }
  }, [value, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  return (
    <Box sx={{
      border: '1px solid #E2E8F0',
      borderRadius: 2,
      bgcolor: disabled ? '#F8FAFC' : '#fff',
      position: 'relative',
      '&:focus-within': { borderColor: '#0073ea', boxShadow: '0 0 0 2px rgba(0,115,234,0.1)' },
      '& .tiptap': {
        outline: 'none',
        padding: '10px 14px',
        minHeight,
        maxHeight: 400,
        overflowY: 'auto',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        color: '#334155',
      },
      '& .tiptap p': { m: 0, mb: '0.5em' },
      '& .tiptap p:last-child': { mb: 0 },
      '& .tiptap ul, & .tiptap ol': { m: 0, pl: '1.5em', mb: '0.5em' },
      '& .tiptap code': { bgcolor: '#F1F5F9', px: '4px', py: '2px', borderRadius: '4px', fontSize: '0.85em', fontFamily: 'monospace' },
      '& .tiptap pre': { bgcolor: '#F1F5F9', p: '12px', borderRadius: '6px', overflow: 'auto', mb: '0.5em' },
      '& .tiptap pre code': { bgcolor: 'transparent', p: 0 },
      '& .tiptap blockquote': { borderLeft: '3px solid #CBD5E1', pl: '12px', ml: 0, color: '#64748B', fontStyle: 'italic' },
      '& .tiptap a': { color: '#0073ea', textDecoration: 'underline' },
      '& .tiptap strong': { fontWeight: 600 },
      '& .tiptap img': { maxWidth: '100%', borderRadius: '6px', margin: '8px 0', cursor: 'pointer' },
      '& .tiptap .is-editor-empty:first-child::before': {
        content: 'attr(data-placeholder)',
        float: 'left',
        color: '#94A3B8',
        pointerEvents: 'none',
        height: 0,
      },
    }}>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />
      <MenuBar
        editor={editor}
        onImageClick={() => imageInputRef.current?.click()}
        onEmojiClick={(e) => setEmojiAnchor(e.currentTarget)}
        onImproveClick={handleImproveText}
        improving={improving}
        showAIImprove={showAIImprove}
      />
      <EditorContent editor={editor} />
      {uploading && (
        <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.9)', px: 1, py: 0.5, borderRadius: 1, border: '1px solid #E2E8F0' }}>
          <CircularProgress size={14} />
          <span style={{ fontSize: 12, color: '#64748B' }}>Enviando imagem...</span>
        </Box>
      )}

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { boxShadow: '0 8px 30px rgba(0,0,0,0.12)', borderRadius: 2 } } }}
      >
        <Picker
          data={data}
          onEmojiSelect={handleEmojiSelect}
          locale="pt"
          theme="light"
          previewPosition="none"
          skinTonePosition="none"
          maxFrequentRows={2}
        />
      </Popover>
    </Box>
  )
})

// Simple markdown → HTML for the AI improve response
function markdownToHtml(md) {
  if (!md) return '<p></p>'
  let html = md
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/~~(.*?)~~/g, '<s>$1</s>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1">')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
  // Paragraphs
  html = html.split('\n').map(line => line.trim() ? `<p>${line}</p>` : '<p></p>').join('')
  return html
}

// HTML to Markdown converter
function htmlToMarkdown(html) {
  if (!html || html === '<p></p>') return ''

  let md = html
    // Images → markdown
    .replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '![]($1)')
    // Bold
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    // Italic
    .replace(/<em>(.*?)<\/em>/gi, '*$1*')
    // Strikethrough
    .replace(/<s>(.*?)<\/s>/gi, '~~$1~~')
    // Inline code
    .replace(/<code>(.*?)<\/code>/gi, '`$1`')
    // Links
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    // Code blocks
    .replace(/<pre><code>(.*?)<\/code><\/pre>/gis, '```\n$1\n```')
    // Blockquote
    .replace(/<blockquote><p>(.*?)<\/p><\/blockquote>/gi, '> $1')
    // Unordered list items
    .replace(/<li><p>(.*?)<\/p><\/li>/gi, '<li>$1</li>')
    .replace(/<ul>(.*?)<\/ul>/gis, (_, content) => {
      return content.replace(/<li>(.*?)<\/li>/gi, '- $1').trim()
    })
    // Ordered list items
    .replace(/<ol>(.*?)<\/ol>/gis, (_, content) => {
      let i = 0
      return content.replace(/<li>(.*?)<\/li>/gi, (_, text) => { i++; return `${i}. ${text}` }).trim()
    })
    // Paragraphs to newlines
    .replace(/<p>(.*?)<\/p>/gi, '$1\n')
    // Break tags
    .replace(/<br\s*\/?>/gi, '\n')
    // Remove remaining HTML
    .replace(/<[^>]+>/g, '')
    // Decode HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Trim trailing whitespace
    .trim()

  return md
}

export default MarkdownEditor
